/* eslint-disable no-underscore-dangle */
import { includes, keys } from 'lodash/fp';
import { useEffect, useState } from 'react';

import { PRODUCT_REVIEW_IDS } from '../../constants';
import { useHasProductReviewScript } from '../../../../application/scripts/product-review-widget-provider';
import browser, { type WindowType } from '../../../../utils/browser';
import getIsServer from '../../../../utils/get-is-server';

type ProductReviewIdType = String;

type InsuranceType = keyof typeof PRODUCT_REVIEW_IDS;

const DIV_ID = '#pr-rich-rating-badge-widget';
const WIDGET_TYPE = 'rich-rating-badge';
const getWidgetSettings = (productReviewId: ProductReviewIdType) => ({
  container: DIV_ID,
  identificationDetails: {
    identifier: productReviewId,
    strategy: 'from-brand-external-entry-id',
    type: 'single',
  },
});

const loadWidget = (windowObject: WindowType, productReviewId: ProductReviewIdType) => {
  // eslint-disable-next-line no-param-reassign
  windowObject.__productReviewCallbackQueue = windowObject.__productReviewCallbackQueue || [];
  windowObject.__productReviewCallbackQueue.push((ProductReview) => {
    ProductReview.use(WIDGET_TYPE, getWidgetSettings(productReviewId));
  });
};

const destroyWidget = (windowObject: WindowType, productReviewId: ProductReviewIdType) => {
  windowObject.__productReviewCallbackQueue.push((ProductReview) => {
    ProductReview.destroy(WIDGET_TYPE, getWidgetSettings(productReviewId));
  });
};

const ProductReviewInlineWidgetScript = ({ insuranceType }: { insuranceType: string }) => {
  const [hasRunLoad, setHasRunLoad] = useState(false);
  const isServer = getIsServer();
  const { hasProductReviewLoaded } = useHasProductReviewScript();
  const productReviewIdKeys = keys(PRODUCT_REVIEW_IDS);
  const windowObject = browser.getWindow() as WindowType;
  const productReviewId = PRODUCT_REVIEW_IDS[insuranceType as InsuranceType];

  useEffect(() => {
    if (isServer || !includes(insuranceType, productReviewIdKeys) || !hasProductReviewLoaded) {
      return undefined;
    }

    if (!hasRunLoad) {
      loadWidget(windowObject, productReviewId);
      setHasRunLoad(true);
    }

    return () => {
      if (hasRunLoad) {
        destroyWidget(windowObject, productReviewId);
        setHasRunLoad(false);
      }
    };
  }, [insuranceType, hasProductReviewLoaded, isServer, hasRunLoad, productReviewIdKeys, windowObject, productReviewId]);

  return null;
};

export default ProductReviewInlineWidgetScript;
